import { authClient } from "@crm/access/auth/client";
import { useAuth } from "@crm/access/hooks/use-auth";
import { createFileRoute, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";
import { WorkbenchShell } from "../../components/workbench-shell";
import { decideProtectedRouteAccess } from "./-lib/protected-route-access";
import { matchWorkbenchRouteRule } from "./-lib/route-permissions";

/**
 * Workbench 认证布局（对应 CRM 的 _protected）。
 * redesign v2：统一外壳（侧栏+顶栏）；ClientAuthProvider 让
 * useAuth()/usePermission() 在 workbench 可用（导航可见性权限驱动）。
 *
 * 路由级权限守卫（Sprint 2 权限漏洞修复）：
 * - 登录后按 workbench 路由权限表（-lib/route-permissions.ts）校验
 * - 学员访问管理页 → 重定向回 /（redirectForPermissionResult 语义：student 回首页）
 * - 非学员无权限 → 显示 403 无权页
 * - 学员可读页（/knowledge/articles 等）默认放行，仅登录校验
 * - /sio-tools/** 依赖页面内课程解锁门禁，路由层不重复
 *
 * 本守卫只做 UI 入口控制；每个 ServerFn handler 仍是权威授权点。
 */
export const Route = createFileRoute("/_protected")({
  component: ProtectedLayout,
});

function ProtectedLayout() {
  const navigate = useNavigate();
  const { data: session, isPending } = authClient.useSession();
  const auth = useAuth();
  const routePath = useRouterState({
    select: (s) => s.matches.at(-1)?.pathname ?? "",
  });
  const decision = decideProtectedRouteAccess({
    isSessionPending: isPending,
    hasSession: Boolean(session),
    auth,
    routePath,
  });

  useEffect(() => {
    if (decision.kind === "redirect-login") {
      void navigate({ to: "/login", replace: true });
    } else if (decision.kind === "redirect-home") {
      void navigate({ to: "/", replace: true });
    }
  }, [decision.kind, navigate]);

  if (decision.kind === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground">加载中…</div>
      </div>
    );
  }

  if (decision.kind === "redirect-login" || decision.kind === "redirect-home") {
    return null;
  }

  if (decision.kind === "forbidden") {
    const rule = matchWorkbenchRouteRule(routePath);
    return (
      <WorkbenchShell>
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-2 p-8 text-center">
          <h1 className="text-2xl font-semibold">403 — 无权访问</h1>
          <p className="text-muted-foreground text-sm">
            此页面需要管理权限（{rule?.anyOf.join(" / ") ?? "未知权限"}），当前账号未获授权。
          </p>
        </div>
      </WorkbenchShell>
    );
  }

  return (
    <WorkbenchShell>
      <Outlet />
    </WorkbenchShell>
  );
}
